import type { BusGroundingIconName } from './icon-names';
import type { IconPathData, PathDataObject } from './icon-paths';

const VIEW_BOX = '0 0 60 60';

const WIRE_STROKE_WIDTH = 2;
const HALF_STROKE = WIRE_STROKE_WIDTH / 2;

const conductor = (d: string): PathDataObject => ({ d });

const wire = (d: string): PathDataObject => ({
    d,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: WIRE_STROKE_WIDTH,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
});

const CENTRE = 30;

const CONDUCTOR_TOP = 28;
const CONDUCTOR_BOTTOM = 32;
const CONDUCTOR_MIDDLE = (CONDUCTOR_TOP + CONDUCTOR_BOTTOM) / 2;
const CONDUCTOR_THICKNESS = CONDUCTOR_BOTTOM - CONDUCTOR_TOP;
const CONDUCTOR_LEFT = 15;
const CONDUCTOR_WIDTH = 30;

const GROUND_REACH = 14;
// The sideways ground symbol has to clear the right-hand end of the bus bar, so it keeps a longer reach.
const SIDEWAYS_GROUND_REACH = 17.5;
const GROUND_BAR_LENGTHS = [16, 10, 4];
const GROUND_BAR_GAP = 3;

const groundBar = (index: number) => CENTRE + GROUND_REACH + index * GROUND_BAR_GAP;
const sidewaysGroundBar = (index: number) => CENTRE + SIDEWAYS_GROUND_REACH + index * GROUND_BAR_GAP;

const RESISTOR_HEIGHT = 7;
const RESISTOR_WIDTH = 7;
const EARTH_RUN = groundBar(0) - HALF_STROKE - CONDUCTOR_BOTTOM;
const RESISTOR_LEAD = (EARTH_RUN - RESISTOR_HEIGHT) / 2;
const RESISTOR_TOP = CONDUCTOR_BOTTOM + RESISTOR_LEAD;
const RESISTOR_BOTTOM = RESISTOR_TOP + RESISTOR_HEIGHT;

const POSITIVE_BOTTOM = CONDUCTOR_MIDDLE - HALF_STROKE - EARTH_RUN;
const POSITIVE_TOP = POSITIVE_BOTTOM - CONDUCTOR_THICKNESS;
const NEGATIVE_TOP = CONDUCTOR_MIDDLE + HALF_STROKE + EARTH_RUN;
const POSITIVE_RESISTOR_TOP = POSITIVE_BOTTOM + RESISTOR_LEAD;
const POSITIVE_RESISTOR_BOTTOM = POSITIVE_RESISTOR_TOP + RESISTOR_HEIGHT;
const NEGATIVE_RESISTOR_TOP = CONDUCTOR_MIDDLE + HALF_STROKE + RESISTOR_LEAD;
const NEGATIVE_RESISTOR_BOTTOM = NEGATIVE_RESISTOR_TOP + RESISTOR_HEIGHT;

const busBar = (top: number) =>
    `M${CONDUCTOR_LEFT} ${top}h${CONDUCTOR_WIDTH}V${top + CONDUCTOR_THICKNESS}H${CONDUCTOR_LEFT}z`;

const resistorBox = (top: number) =>
    `M${CENTRE - RESISTOR_WIDTH / 2} ${top}h${RESISTOR_WIDTH}V${top + RESISTOR_HEIGHT}h-${RESISTOR_WIDTH}z`;

const GROUND_SYMBOL = GROUND_BAR_LENGTHS.map(
    (length, index) => `M${CENTRE - length / 2} ${groundBar(index)}h${length}`,
).join('');

const SIDEWAYS_GROUND_SYMBOL = GROUND_BAR_LENGTHS.map(
    (length, index) => `M${sidewaysGroundBar(index)} ${CENTRE - length / 2}v${length}`,
).join('');

const SINGLE_CONDUCTOR = conductor(busBar(CONDUCTOR_TOP));

const POSITIVE_CONDUCTOR = conductor(busBar(POSITIVE_TOP));
const NEGATIVE_CONDUCTOR = conductor(busBar(NEGATIVE_TOP));

const SOLID_PATH = wire(`M${CENTRE} ${CONDUCTOR_BOTTOM}V${groundBar(0)}${GROUND_SYMBOL}`);

const IMPEDANCE_PATH = wire(
    `M${CENTRE} ${CONDUCTOR_BOTTOM}V${RESISTOR_TOP}M${CENTRE} ${RESISTOR_BOTTOM}V${groundBar(0)}${GROUND_SYMBOL}`,
);
const IMPEDANCE_RESISTOR = wire(resistorBox(RESISTOR_TOP));

const SYMMETRICAL_PATH = wire(
    `M${CENTRE} ${POSITIVE_BOTTOM}V${POSITIVE_RESISTOR_TOP}` +
        `M${CENTRE} ${POSITIVE_RESISTOR_BOTTOM}V${NEGATIVE_RESISTOR_TOP}` +
        `M${CENTRE} ${NEGATIVE_RESISTOR_BOTTOM}V${NEGATIVE_TOP}` +
        `M${CENTRE} ${CONDUCTOR_MIDDLE}H${sidewaysGroundBar(0)}` +
        SIDEWAYS_GROUND_SYMBOL,
);
const SYMMETRICAL_RESISTORS = wire(`${resistorBox(POSITIVE_RESISTOR_TOP)}${resistorBox(NEGATIVE_RESISTOR_TOP)}`);

const SOLIDLY_GROUNDED_BUS: IconPathData = {
    viewBox: VIEW_BOX,
    fillsCell: true,
    paths: [SINGLE_CONDUCTOR, SOLID_PATH],
};

const IMPEDANCE_GROUNDED_BUS: IconPathData = {
    viewBox: VIEW_BOX,
    fillsCell: true,
    paths: [SINGLE_CONDUCTOR, IMPEDANCE_PATH, IMPEDANCE_RESISTOR],
};

const SYMMETRICALLY_GROUNDED_BUS: IconPathData = {
    viewBox: VIEW_BOX,
    fillsCell: true,
    paths: [POSITIVE_CONDUCTOR, NEGATIVE_CONDUCTOR, SYMMETRICAL_PATH, SYMMETRICAL_RESISTORS],
};

const BUS_GROUNDING_ICON_PATHS: Record<BusGroundingIconName, IconPathData> = {
    solidlyGroundedBus: SOLIDLY_GROUNDED_BUS,
    impedanceGroundedBus: IMPEDANCE_GROUNDED_BUS,
    symmetricallyGroundedBus: SYMMETRICALLY_GROUNDED_BUS,
};

export { BUS_GROUNDING_ICON_PATHS };
